import { useState } from "react"
import {
    Markdown,
    OutlinedButton,
    Text,
    Title,
    ToggleSwitchButton
} from "dash-styled"
import { JSONSchema7 } from "json-schema"
import { Table } from "resha"
import { ReactComponent as AddIcon } from "icons/lib/plus.svg"
import { ReactComponent as TrashIcon } from "icons/lib/trash.svg"
import { ReactComponent as EditIcon } from "icons/lib/edit.svg"
import { ReactComponent as DragIcon } from "icons/lib/drag.svg"

import { SuperTaskWorkerModel } from "../../../../../../../../../../../../Models/API/SuperTaskModel"
import { StaticAccordion } from "../../../../../../../../../Common/StaticAccordion/StaticAccordion"
import { SchemaFormDeleteModal } from "../../../../../../../../../../../Common/SchemaForm/Modals"
import { WorkerModal, WorkerModalType } from "./WorkerModal"

type TaskWorkersProps = {
    schema: JSONSchema7
    workers: SuperTaskWorkerModel[]
    disabled?: boolean
    onChange: (workers: SuperTaskWorkerModel[]) => void
}

type WorkerModalOptions = {
    visible: boolean
    worker?: number
}

enum Status {
    Active = "Active",
    Inactive = "Inactive"
}

const IconWrapper =
    (Icon: React.FC<React.SVGProps<SVGSVGElement>>, noMargin = false) =>
    ({ className, ...rest }: React.SVGProps<SVGSVGElement>) =>
        <Icon className={`w-4 h-4  ${noMargin ? "" : className}`} {...rest} />

const TaskWorkers = ({
    schema,
    workers,
    disabled = false,
    onChange
}: TaskWorkersProps) => {
    const [workerModal, setWorkerModal] = useState<WorkerModalOptions>({
        visible: false
    })
    const [deleteModal, setDeleteModal] = useState<WorkerModalOptions>({
        visible: false
    })

    const handleOpenWorkerModal = (worker?: number) => () => {
        setWorkerModal({
            visible: true,
            worker
        })
    }

    const handleCloseWorkerModal = () => {
        setWorkerModal({
            visible: false
        })
    }

    const handleOpenDeleteModal = (worker: number) => () => {
        setDeleteModal({
            visible: true,
            worker
        })
    }

    const handleCloseDeleteModal = () => {
        setDeleteModal({
            visible: false
        })
    }

    const handleDeleteWorker = () => {
        if (!disabled) {
            const workerIndex = deleteModal?.worker
            if (workerIndex !== undefined) {
                let newWorkers = [...(workers || [])]
                newWorkers.splice(workerIndex, 1)
                onChange(newWorkers)
            }
        }
    }

    const handleChangeWorker = (workerData: SuperTaskWorkerModel) => {
        if (!disabled) {
            const workerIndex = workerModal?.worker
            let newWorkers = [...(workers || [])]
            if (workerIndex !== undefined) {
                newWorkers.splice(workerIndex, 1, workerData)
            } else {
                newWorkers = [...newWorkers, workerData]
            }
            onChange(newWorkers)
        }
    }

    const handleWorkerStatusChange =
        (worker: number, status: boolean) => () => {
            if (!disabled) {
                const newWorkers = [...(workers || [])]
                newWorkers.splice(worker, 1, {
                    ...newWorkers[worker],
                    active: !status
                })
                onChange(newWorkers)
            }
        }

    const handleDrop = () => (workers: SuperTaskWorkerModel[]) => {
        if (!disabled) {
            onChange(workers)
        }
    }

    return (
        <>
            <StaticAccordion
                withFooter
                title="Workers"
                titleComponent="h5"
                className="shadow-lg"
                bodyClassName="pt-6 px-4 bg-white rounded-b-large shadow-inner"
            >
                {!disabled && (
                    <div className="flex mb-4">
                        <OutlinedButton
                            Icon={AddIcon}
                            label="Add new worker"
                            marginLeft="2"
                            className="bg-white py-3"
                            onClick={handleOpenWorkerModal()}
                        />
                    </div>
                )}

                <Table
                    draggable
                    draggableColIndex={0}
                    className="w-full"
                    rowBgColor=""
                    style={{
                        borderSpacing: "0 4px"
                    }}
                    getKey={() => null}
                    items={workers}
                    withBorders={false}
                    onDrop={!disabled ? handleDrop() : undefined}
                    columns={[
                        {
                            className: "w-1/12 pl-2 border-b-transparent",
                            renderHeader: () => null,
                            render: () => {
                                return <DragIcon className="w-8 h-8" />
                            }
                        },
                        {
                            className: "w-5/12 border-b-transparent",
                            renderHeader: () => (
                                <Text color="gray" className="m-0">
                                    Name & Description
                                </Text>
                            ),
                            render: ({ name, description }) => {
                                return (
                                    <>
                                        <Title
                                            component="h6"
                                            variant="heading4"
                                            className="capitalize m-0"
                                        >
                                            {name}
                                        </Title>
                                        {description && (
                                            <Markdown text={description} />
                                        )}
                                    </>
                                )
                            }
                        },
                        {
                            className: "w-2/12 border-b-transparent",
                            renderHeader: () => (
                                <Text color="gray" className="m-0">
                                    Type
                                </Text>
                            ),
                            render: ({ type }) => {
                                return (
                                    <Text className="capitalize">{type}</Text>
                                )
                            }
                        },
                        {
                            className: "w-2/12 border-b-transparent",
                            renderHeader: () => (
                                <Text color="gray" className="m-0">
                                    Status
                                </Text>
                            ),
                            render: ({ active }, isChecked, workerIndex) => {
                                return (
                                    <div className="flex">
                                        <ToggleSwitchButton
                                            fullWidth={false}
                                            iconSize="lg"
                                            className="w-auto"
                                            offClass=""
                                            value={active}
                                            activeColor="#00B092"
                                            inactiveColor="#EA5757"
                                            onChange={handleWorkerStatusChange(
                                                workerIndex,
                                                active
                                            )}
                                        />
                                        <Text>
                                            {active
                                                ? Status.Active
                                                : Status.Inactive}
                                        </Text>
                                    </div>
                                )
                            }
                        },
                        {
                            className: "w-2/12 pr-2 border-b-transparent",
                            renderHeader: () => null,
                            render: (value, isChecked, workerIndex) => {
                                return (
                                    <div className="flex items-center justify-between">
                                        <div className="h-4 border-l"></div>
                                        <div className="flex items-center">
                                            <OutlinedButton
                                                label="Edit"
                                                marginLeft="2"
                                                className="py-3 bg-white"
                                                disabled={disabled}
                                                Icon={IconWrapper(EditIcon)}
                                                onClick={handleOpenWorkerModal(
                                                    workerIndex
                                                )}
                                            />
                                            <OutlinedButton
                                                label=""
                                                marginLeft="2"
                                                className="py-3 px-3 bg-white"
                                                disabled={disabled}
                                                Icon={IconWrapper(
                                                    TrashIcon,
                                                    true
                                                )}
                                                onClick={handleOpenDeleteModal(
                                                    workerIndex
                                                )}
                                            />
                                        </div>
                                    </div>
                                )
                            }
                        }
                    ]}
                />
            </StaticAccordion>
            {workerModal?.visible && (
                <WorkerModal
                    type={
                        workerModal?.worker !== undefined
                            ? WorkerModalType.Edit
                            : WorkerModalType.New
                    }
                    schema={schema}
                    onSubmit={handleChangeWorker}
                    onClose={handleCloseWorkerModal}
                    index={workerModal?.worker}
                    value={
                        workerModal.worker !== undefined
                            ? workers[workerModal?.worker]
                            : undefined
                    }
                />
            )}
            {deleteModal.visible && (
                <SchemaFormDeleteModal
                    onClose={handleCloseDeleteModal}
                    onSubmit={handleDeleteWorker}
                />
            )}
        </>
    )
}

export default TaskWorkers
